import React, { useState } from "react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MeetingWidget from "@/components/MeetingWidget";
import { useLanguage } from '@/lib/i18n';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  uploadInquiryFiles,
  validateInquiryFiles
} from "@/lib/awsS3Storage";
import { createInquiry } from "@/lib/inquiries";
import { saveInquiryToS3 } from '@/lib/inquiries';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "@/components/ui/select";
import { sendInquiryNotificationEmail } from '@/lib/emailNotifications';

const fieldClass = "border rounded px-2 py-1 w-full";
const AGB_VERSION = 'v2026-06';

/* ───────── Pflegekräfte-Formular ───────── */
const NurseForm = () => {
  const { toast } = useToast();
  const { tr } = useLanguage();
  const [nurseQualification, setNurseQualification] = useState("");
  const [nurseLanguage, setNurseLanguage] = useState("");
  const [consent, setConsent] = useState(false);

  const getUploadedFiles = (fd: FormData): File[] =>
    fd.getAll("attachments").filter((e): e is File => e instanceof File);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const files = getUploadedFiles(fd);

    const validation = validateInquiryFiles(files);
    if (!validation.ok) {
      toast({ title: "Fehler", description: validation.error });
      return;
    }

    let uploadedPaths: string[] = [];
    if (files.length > 0) {
      try {
        uploadedPaths = await uploadInquiryFiles(files, "nurse");
      } catch (error: any) {
        toast({ title: "Upload fehlgeschlagen", description: error?.message || "Bitte erneut versuchen." });
        return;
      }
    }

    const newInquiry = createInquiry("nurse", {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      qualification: nurseQualification,
      languageLevel: nurseLanguage,
      phone: String(fd.get("phone") ?? ""),
      message: String(fd.get("message") ?? ""),
      attachmentNames: files.map((f) => f.name).join(", "),
      attachmentPaths: uploadedPaths.join(", ")
    });

    // Anfrage auch auf S3 speichern
    try {
      await saveInquiryToS3(newInquiry);
    } catch (err) {
      toast({ title: "Warnung", description: "Anfrage konnte nicht auf S3 gespeichert werden." });
    }

    // EmailJS Benachrichtigung
    try {
      await sendInquiryNotificationEmail({
        inquiryType: 'Bewerbung (Pflegekraft)',
        fromName: String(fd.get("name") ?? ""),
        fromEmail: String(fd.get("email") ?? ""),
        phone: String(fd.get("phone") ?? ""),
        message: String(fd.get("message") ?? ""),
        details: `Qualifikation: ${nurseQualification}, Sprache: ${nurseLanguage}, Anhänge: ${files.map(f => f.name).join(", ")}`
      });
    } catch (err) {
      toast({ title: "E-Mail Fehler", description: "Benachrichtigung konnte nicht gesendet werden." });
    }

    toast({ title: "Erfolgreich", description: "Ihre Anfrage wurde gesendet." });
    form.reset();
    setNurseQualification("");
    setNurseLanguage("");
    setConsent(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="nurse-name">{tr('contactForm.nurses.name')} *</Label>
          <input id="nurse-name" name="name" required maxLength={100} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="nurse-email">{tr('contactForm.nurses.email')}</Label>
          <input id="nurse-email" name="email" type="email" maxLength={255} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label>{tr('contactForm.nurses.qualification')} *</Label>
          <Select value={nurseQualification} onValueChange={setNurseQualification} required>
            <SelectTrigger className={fieldClass}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="diplom-examinierte-pflegekraft">{tr('contactForm.nurses.qual.diploma')}</SelectItem>
              <SelectItem value="pflegeassistent">{tr('contactForm.nurses.qual.assistant')}</SelectItem>
              <SelectItem value="andere">{tr('contactForm.nurses.qual.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{tr('contactForm.nurses.language')} *</Label>
          <Select value={nurseLanguage} onValueChange={setNurseLanguage} required>
            <SelectTrigger className={fieldClass}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A1/A2">A1/A2</SelectItem>
              <SelectItem value="B1/B2">B1/B2</SelectItem>
              <SelectItem value="C1-hoher">C1 und höher</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="nurse-phone">{tr('contactForm.nurses.phone')}</Label>
          <input id="nurse-phone" name="phone" maxLength={30} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="nurse-message">{tr('contactForm.nurses.message')}</Label>
          <Textarea id="nurse-message" name="message" maxLength={1000} className={fieldClass} />
        </div>
        <div className="col-span-2 flex items-center space-x-2 mt-2">
          <input type="checkbox" id="nurse-consent" checked={consent} onChange={e => setConsent(e.target.checked)} required />
          <Label htmlFor="nurse-consent">{tr('contactForm.gdpr')}</Label>
          <a
            href="https://tally.so/r/7RZ9M2"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline ml-2"
          >
            {tr('contactForm.onlineConsent')}
          </a>
        </div>
        <div className="space-y-2">
          <Label htmlFor="nurse-attachments">{tr('contactForm.upload.label')}</Label>
          <input type="file" name="attachments" multiple className={fieldClass} />
        </div>
      </div>
      <Button type="submit" className="mt-4">{tr('contactForm.nurses.submit')}</Button>
    </form>
  );
};

/* ───────── Einrichtungen-Formular ───────── */
const EmployerForm = () => {
  const { toast } = useToast();
  const { tr } = useLanguage();
  const [staffType, setStaffType] = useState("");
  const [consent, setConsent] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const getUploadedFiles = (fd: FormData): File[] =>
    fd.getAll("attachments").filter((e): e is File => e instanceof File);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const files = getUploadedFiles(fd);

    if (!agbAccepted) {
      toast({ title: "Fehler", description: tr('contactForm.employers.agbRequired') });
      return;
    }

    const validation = validateInquiryFiles(files);
    if (!validation.ok) {
      toast({ title: "Fehler", description: validation.error });
      return;
    }

    let uploadedPaths: string[] = [];
    if (files.length > 0) {
      try {
        uploadedPaths = await uploadInquiryFiles(files, "employer");
      } catch (error: any) {
        toast({ title: "Upload fehlgeschlagen", description: error?.message || "Bitte erneut versuchen." });
        return;
      }
    }

    const newInquiry = createInquiry("employer", {
      facilityName: String(fd.get("facilityName") ?? ""),
      contactPerson: String(fd.get("contactPerson") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      staffCount: String(fd.get("staffCount") ?? ""),
      staffType,
      message: String(fd.get("message") ?? ""),
      agbAccepted,
      agbVersion: AGB_VERSION,
      agbAcceptedAt: new Date().toISOString(),
      attachmentNames: files.map((f) => f.name).join(", "),
      attachmentPaths: uploadedPaths.join(", ")
    });

    // Anfrage auch auf S3 speichern
    try {
      await saveInquiryToS3(newInquiry);
    } catch (err) {
      toast({ title: "Warnung", description: "Anfrage konnte nicht auf S3 gespeichert werden." });
    }

    // EmailJS Benachrichtigung
    try {
      await sendInquiryNotificationEmail({
        inquiryType: 'Anfrage (Arbeitgeber)',
        fromName: String(fd.get("contactPerson") ?? ""),
        fromEmail: String(fd.get("email") ?? ""),
        phone: String(fd.get("phone") ?? ""),
        message: String(fd.get("message") ?? ""),
        details: `Einrichtung: ${String(fd.get("facilityName") ?? "")}, Pflegekräfte: ${staffType}, Anzahl: ${String(fd.get("staffCount") ?? "")}, Anhänge: ${files.map(f => f.name).join(", ")}`
      });
    } catch (err) {
      toast({ title: "E-Mail Fehler", description: "Benachrichtigung konnte nicht gesendet werden." });
    }

    toast({ title: "Erfolgreich", description: "Ihre Anfrage wurde gesendet." });
    form.reset();
    setStaffType("");
    setConsent(false);
    setAgbAccepted(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="emp-facility">{tr('contactForm.employers.name')} *</Label>
          <input id="emp-facility" name="facilityName" required maxLength={150} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-contact">{tr('contactForm.employers.contact')} *</Label>
          <input id="emp-contact" name="contactPerson" required maxLength={100} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-email">{tr('contactForm.employers.email')} *</Label>
          <input id="emp-email" name="email" type="email" required maxLength={255} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-phone">{tr('contactForm.employers.phone')}</Label>
          <input id="emp-phone" name="phone" maxLength={30} className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-staffCount">{tr('contactForm.employers.count')} *</Label>
          <input id="emp-staffCount" name="staffCount" type="number" min="1" required className={fieldClass} />
        </div>
        <div className="space-y-2">
          <Label>{tr('contactForm.employers.needType')} *</Label>
          <Select value={staffType} onValueChange={setStaffType} required>
            <SelectTrigger className={fieldClass}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="examinierte-pflegekraefte">{tr('contactForm.employers.need.hospital')}</SelectItem>
              <SelectItem value="pflegeassistenten">{tr('contactForm.employers.need.elderly')}</SelectItem>
              <SelectItem value="beides">{tr('contactForm.employers.need.intensive')}</SelectItem>
              <SelectItem value="andere">{tr('contactForm.employers.need.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="emp-message">{tr('contactForm.employers.submit')}</Label>
          <Textarea id="emp-message" name="message" maxLength={1000} className={fieldClass} />
        </div>
        <div className="col-span-2 flex items-center space-x-2 mt-2">
          <input type="checkbox" id="emp-consent" checked={consent} onChange={e => setConsent(e.target.checked)} required />
          <Label htmlFor="emp-consent">{tr('contactForm.gdpr')}</Label>
          <a
            href="https://tally.so/r/7RZ9M2"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline ml-2"
          >
            Einverständniserklärung online unterschreiben
          </a>
        </div>
        <div className="col-span-2 flex items-start gap-2 mt-1">
          <input
            type="checkbox"
            id="emp-agb"
            checked={agbAccepted}
            onChange={(e) => setAgbAccepted(e.target.checked)}
            required
          />
          <Label htmlFor="emp-agb" className="leading-relaxed">
            {tr('contactForm.employers.agb')}{' '}
            <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              {tr('contactForm.employers.agbLink')}
            </a>{' '}
            <span className="text-muted-foreground">({tr('contactForm.employers.agbVersion')}: {AGB_VERSION})</span>
          </Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-attachments">{tr('contactForm.upload.label')}</Label>
          <input type="file" name="attachments" multiple className={fieldClass} />
        </div>
      </div>
      <Button type="submit" className="mt-4">{tr('contactForm.employers.submit')}</Button>
    </form>
  );
};

/* ───────── Dual-Tab Wrapper ───────── */
const ContactFormSection = () => {
  const { tr } = useLanguage();
  return (
    <section id="contact-form" className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
          {tr('contactForm.sectionTitle')}
        </h2>
        <div className="flex justify-center mb-8">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="default" size="lg">
                {tr('contactForm.bookConsultation')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl w-full">
              <DialogHeader>
                <DialogTitle>{tr('contactForm.bookConsultation')}</DialogTitle>
              </DialogHeader>
              <MeetingWidget />
            </DialogContent>
          </Dialog>
        </div>
        <Tabs defaultValue="nurses" className="max-w-2xl mx-auto">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="nurses">{tr('contactForm.tabNurses')}</TabsTrigger>
            <TabsTrigger value="employers">{tr('contactForm.tabEmployers')}</TabsTrigger>
          </TabsList>
          <TabsContent value="nurses" className="bg-background p-6 rounded-lg shadow-md">
            <NurseForm />
          </TabsContent>
          <TabsContent value="employers" className="bg-background p-6 rounded-lg shadow-md">
            <EmployerForm />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ContactFormSection;
